<template>
  <b-card-body class="report-lead shadow rounded p-4">
    <validation-observer ref="observer" v-slot="{ handleSubmit }">
      <b-form @submit.prevent="handleSubmit(submit)">
        <div class="row">
          <div class="col-12 col-lg-6">
            <validation-provider
              v-slot="{validated, errors, dirty, valid, invalid}"
              :rules="(action === 'report') ? 'required' : ''"
            >
              <b-form-group
                v-if="action==='report'"
                :label-class="'font-weight-bold'"
                :label="$t('Please choose carefully from the following reasons')"
                :state="(dirty || validated) ? (!!valid) : null"
              >
                <div v-if="isUKLead">
                  <b-form-radio
                    v-model="reason"
                    name="report-reason"
                    value="hoaxlead"
                    :state="(dirty || validated) ? (valid ? null : false) : null"
                  >
                    {{ $t('Hoax/prank leads') }}
                  </b-form-radio>
                  <b-form-radio
                    v-model="reason"
                    name="report-reason"
                    value="fraud"
                    :state="(dirty || validated) ? (valid ? null : false) : null"
                  >
                    {{ $t('Fraudulent activity') }}
                  </b-form-radio>
                </div>
                <b-form-radio
                  v-if="!isUKLead"
                  v-model="reason"
                  name="report-reason"
                  value="invalidphone"
                  :state="(dirty || validated) ? (valid ? null : false) : null"
                >
                  {{ $t('The telephone number is invalid') }}
                </b-form-radio>
                <b-form-radio
                  v-if="!isUKLead"
                  v-model="reason"
                  name="report-reason"
                  value="nocontact"
                  :state="(dirty || validated) ? (valid ? null : false) : null"
                >
                  {{ $t('I cannot contact the customer by phone or email') }}
                </b-form-radio>
                <div v-if="['nocontact', 'nocontacttried', 'nocontactnottried'].includes(reason)" class="pl-3 m-2">
                  <div class="my-3 font-weight-bold reason-type">
                    {{ $t('contact_customer') }}
                  </div>
                  <b-form-group :state="(dirty || validated) ? (!!valid) : null">
                    <b-form-radio
                      v-model="reason"
                      name="report-reason"
                      value="nocontacttried"
                      :state="(dirty || validated) ? (valid ? (manualInvalid ? false : null) : false) : null"
                    >
                      {{ $t('I have tried calling at evenings, the weekend, and during the daytime.') }}
                    </b-form-radio>
                    <b-form-radio
                      v-if="!isExtended"
                      v-model="reason"
                      name="report-reason"
                      value="nocontactnottried"
                      :state="(dirty || validated) ? (valid ? (manualInvalid ? false : null) : false) : null"
                    >
                      {{ $t("You didn't give me enough time to contact the customer before my time for reporting expires.") }}
                    </b-form-radio>
                  </b-form-group>
                </div>
                <b-form-radio
                  v-if="!isUKLead"
                  v-model="reason"
                  name="report-reason"
                  value="wrongwork"
                  :state="(dirty || validated) ? (valid ? null : false) : null"
                >
                  {{ $t('Incorrect lead type (e.g. new boiler was stated but the customer only wanted a boiler service)') }}
                </b-form-radio>
                <b-form-radio
                  v-model="reason"
                  name="report-reason"
                  value="duplicate"
                  :state="(dirty || validated) ? (valid ? null : false) : null"
                >
                  {{ $t('Duplicate job lead (e.g. you have already been sent this lead)') }}
                </b-form-radio>
                <b-form-radio
                  v-model="reason"
                  name="report-reason"
                  value="reject_other"
                  :state="(dirty || validated) ? (valid ? null : false) : null"
                >
                  {{ $t('Other') }}
                </b-form-radio>
                <b-form-invalid-feedback :state="invalid || manualInvalid" :force-show="invalid || manualInvalid">
                  {{ errors[0] }} {{ manualErrors }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <validation-provider v-slot="{validated, errors, dirty, valid, invalid}" rules="required|min:10">
              <b-form-group
                :label-class="'font-weight-bold pt-3'"
                :label="$t('In order to process your returned lead effectively, please include as much detail as possible about what happened.')"
              >
                <b-textarea
                  v-model.trim="description"
                  :rows="5"
                  :state="(dirty || validated) ? (!!valid) : null"
                />
                <b-form-invalid-feedback
                  :state="invalid"
                  :force-show="invalid"
                >
                  {{ errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>
          </div>
          <div class="col-12 col-lg-6">
            <div class="row">
              <div class="col-12 col-xxl-7 offset-xxl-5 col-xl-8 offset-xl-4">
                <b-alert variant="warning" :show="true">
                  <strong>Important points to note</strong>
                  <ol class="small pt-3 pl-0">
                    <li>
                      {{ $t('report_help_1') }}
                    </li>
                    <li class="mt-3">
                      {{ $t('report_help_2') }}
                    </li>
                  </ol>
                </b-alert>
              </div>
            </div>
          </div>

          <div class="col-12">
            <div v-if="!busy">
              <b-button size="sm" variant="primary" @click.prevent="submit">
                {{ buttonText }}
              </b-button>
            </div>
            <div v-else class="text-center">
              <spinner :saving="true" />
            </div>
          </div>
        </div>
      </b-form>
    </validation-observer>
  </b-card-body>
</template>

<script>
import {ValidationProvider, ValidationObserver} from 'vee-validate';
import {ACTIVE_LEAD} from "@/store/leads/mutation-types";
import Spinner from "@/components/animation/Spinner";

export default {
  name: "ReportLead",
  components: {Spinner, ValidationProvider, ValidationObserver},
  props: {
    action: {
      type: String,
      default: 'report',
    }
  },
  data: function () {
    return {
      reason: null,
      description: null,
      error: false,
      manualInvalid: null,
      manualErrors: null,
      busy: false,
    };
  },
  computed: {
    buttonText: function () {
      return (this.action === 'report') ? this.$t('Submit your report') : this.$t('Submit your appeal')
    },
    lead: {
      get: function() {
        return this.$store.state.leads.ACTIVE_LEAD;
      },
      set: function(value) {
        this.$store.commit(ACTIVE_LEAD, value);
      }
    },
    isExtended:  function() {
      return this.lead.reason === null && this.lead.extensions_granted === true;
    },
    isUKLead: function () {
      return this.lead.company.country.iso_code === 'gb';
    },
  },
  methods: {
    submit: function () {
      this.manualInvalid = null;
      this.manualErrors = null;
      this.busy = true;
      this.$refs.observer.validate().then(success => {
        if(!success) {
          this.busy = false;
          return;
        }
        if(this.reason === 'nocontact'){
          this.manualInvalid = true;
          this.manualErrors = this.$t("Please tell us why you are unable to contact the customer.");
          this.busy = false;
          return;
        }
        this.send();
      });
    },
    send: function() {
      if (this.action === 'report') {
        this.$api.leads.reportLead(this.reason, this.description, this.lead.code).then(() => {
          this.$router.push({name: 'my-lead', params: {code: this.lead.code}});
          this.busy = false;
        }).catch(error => {
          this.error = error.response.data.errors[0];
          this.busy = false;
        });
      } else {
        this.$api.leads.appealLead(this.description, this.lead.code).then(() => {
          this.$router.push({name: 'my-lead', params: {code: this.lead.code}});
          this.busy = false;
        }).catch(error => {
          this.error = error.response.data.errors[0];
          this.busy = false;
        });
      }
    }
  },
}
</script>

<style scoped lang="scss">
::v-deep .alert-warning {
  ol {
    list-style-position: inside;
  }
}

.custom-control {
  padding-left: 3rem;
}

.reason-type {
  font-size: $font-size-14;
}

::v-deep .custom-radio {
  .custom-control-label {
    line-height: 1.6;
    padding-top: 3px;
    margin-top: 15px;
    padding-left: 10px;
  }
}


</style>
